// Google imports (20260918110000_google_pull.sql) — calendar:full only.
//
// The list comes from google_imports(), not the table: an ignored import is off
// the calendar for everyone under RLS, but this tab still has to show it. The
// sync itself is the calendar-sync Edge Function, called as the signed-in person
// (it checks calendar:full again, server-side).
import { supabase } from './supabase';
import type { Audience, EventTimes } from './calendarEvents';

type DbError = { message: string } | null;
/** A list read: what came back, and the error when it didn't. A reader that
 *  answered `data ?? []` turned a failed request into "Nothing imported yet" and,
 *  on the failure log itself, "No failures" (WS-V1). */
type Read<T> = { data: T[]; error: DbError };
// No backend configured is not a failed read: the shell renders without one
// (lib/supabase.ts), so the lists are empty rather than in error.
const NO_BACKEND = { data: [], error: null };

export type GoogleImport = EventTimes & {
  id: string;
  title: string;
  type: string;
  location: string | null;
  audience: Audience;
  department_ids: string[];
  ignored: boolean;
};

export type SyncFailure = {
  id: string;
  kind: 'pull' | 'push';
  event_id: string | null;
  /** The Hyprflow event a push failed for (null for the pull, or a general push failure). */
  event: { title: string } | null;
  error: string;
  attempts: number;
  first_failed_at: string;
  last_failed_at: string;
  resolved_at: string | null;
};

/** What "Sync now" got back: not set up yet, what each half did, or an error. */
export type SyncResult =
  | { ok: true; configured: false }
  | {
      ok: true;
      configured: true;
      pull?: { added: number; moved: number; cancelled: number };
      push?: { pushed: number; failed: number };
    }
  | { ok: false; error: string };

export async function listImports(): Promise<Read<GoogleImport>> {
  if (!supabase) return NO_BACKEND;
  const { data, error } = await supabase.rpc('google_imports');
  return { data: (data as GoogleImport[] | null) ?? [], error };
}

export async function setImport(
  id: string,
  v: Pick<GoogleImport, 'ignored' | 'type' | 'audience' | 'department_ids'>,
): Promise<{ error: DbError }> {
  if (!supabase) return { error: { message: 'Not connected.' } };
  const { error } = await supabase.rpc('set_google_import', {
    p_id: id,
    p_ignored: v.ignored,
    p_type: v.type,
    p_audience: v.audience,
    p_department_ids: v.audience === 'department' ? v.department_ids : [],
  });
  return { error };
}

/** The last 20 failing streaks, open or resolved, newest first. */
export async function listFailures(): Promise<Read<SyncFailure>> {
  if (!supabase) return NO_BACKEND;
  const { data, error } = await supabase
    .from('calendar_sync_failures')
    .select(
      'id, kind, event_id, error, attempts, first_failed_at, last_failed_at, resolved_at, event:calendar_events(title)',
    )
    .order('last_failed_at', { ascending: false })
    .limit(20);
  return { data: (data as SyncFailure[] | null) ?? [], error };
}

/** Hyprflow events not in Google — the "Not in Google yet" marker. The rule lives
 *  in calendar_not_in_google() (20260918120000_google_push.sql). With the error:
 *  a failed check read as "every event is in Google". */
export async function notInGoogle(): Promise<{ ids: Set<string>; error: DbError }> {
  if (!supabase) return { ids: new Set(), error: null };
  const { data, error } = await supabase.rpc('calendar_not_in_google');
  return { ids: new Set((data as string[] | null) ?? []), error };
}

export async function syncNow(): Promise<SyncResult> {
  if (!supabase) return { ok: false, error: 'Not connected.' };
  const { data, error } = await supabase.functions.invoke('calendar-sync', {
    body: { mode: 'sync' },
  });
  // A failed pull comes back as 200 + ok:false; an error here is the function
  // itself unreachable (or refusing the caller).
  if (error) return { ok: false, error: 'Could not reach the sync service.' };
  return data as SyncResult;
}
